import type { LumberPiecesByProfile } from "building-types/framing/lumberTypes";
import { RoofFramingComponentType } from "building-types/framing/roofFramingComponentTypes";
import { TOP_CHORD_CLIP_EXTENSION } from "building-types/framing/roofFramingConstants";
import type { RoofTrussBasicInput, TrussMemberSpanData } from "building-types/framing/roofTrussTypes";
import type { UnsignedNumber } from "common/types/nominalNumbersTypes";
import { lumberRailNew } from "doc/utils/lumberTools";
import {
  planeNewXZ,
  planeProjectPolyLine3,
  polyLine2OnPlane,
  planeNew,
  planeMoveAlongNormal,
  planeCopy,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine3New } from "geom/polyLine3";
import type { PolyLine3 } from "geom/polyLine3";
import { vec2Normalize } from "geom/vec2";
import { vec3Cross } from "geom/vec3";

export function trussBasicInputNew({
  trussType,
  leftSpan,
  leftEaveFasciaSize,
  rightSpan,
  rightEaveFasciaSize,
  leftOverhang,
  rightOverhang,
  leftTopChordPitch,
  rightTopChordPitch,
  topChordProfile,
  bottomChordProfile,
  webChordProfile,
  ridgeRafterProfile,
  bottomChordOffset,
  leftBearingWidth,
  rightBearingWidth,
  trussPlane,
}: RoofTrussBasicInput): RoofTrussBasicInput {
  return {
    leftSpan,
    leftEaveFasciaSize,
    rightSpan,
    rightEaveFasciaSize,
    leftOverhang,
    rightOverhang,
    leftTopChordPitch,
    rightTopChordPitch,
    topChordProfile,
    bottomChordProfile,
    webChordProfile,
    ridgeRafterProfile,
    bottomChordOffset,
    leftBearingWidth,
    rightBearingWidth,
    trussType,
    trussPlane,
  };
}

export function generateTopChords(
  leftSpan: UnsignedNumber,
  rightSpan: UnsignedNumber,
  leftTopChordPitch: UnsignedNumber,
  rightTopChordPitch: UnsignedNumber,
  verticalOffsetLeft: number = 0,
  verticalOffsetRight: number = 0,
): { left: PolyLine3; right: PolyLine3 } {
  const leftTopChords = polyLine3New([
    -leftSpan,
    0,
    -leftSpan * leftTopChordPitch + verticalOffsetLeft,
    0,
    0,
    verticalOffsetLeft,
  ]);
  const rightTopChords = polyLine3New([
    0,
    0,
    verticalOffsetRight,
    rightSpan,
    0,
    -rightSpan * rightTopChordPitch + verticalOffsetRight,
  ]);

  return { left: leftTopChords, right: rightTopChords };
}

export function getBottomChordTopZ(
  leftSpan: UnsignedNumber,
  rightSpan: UnsignedNumber,
  leftOverhang: UnsignedNumber,
  rightOverhang: UnsignedNumber,
  leftTopChordPitch: UnsignedNumber,
  rightTopChordPitch: UnsignedNumber,
  topChordHeight: UnsignedNumber,
  bottomChordHeight: UnsignedNumber,
  bottomChordOffset: number,
): { bottomChordLeftTopZ: number; bottomChordRightTopZ: number; bottomChordTopZ: number } {
  const bottomChordLeftTopZ =
    -(leftSpan - leftOverhang) * leftTopChordPitch -
    topChordHeight * Math.sqrt(1 + leftTopChordPitch * leftTopChordPitch) +
    bottomChordOffset +
    bottomChordHeight;

  const bottomChordRightTopZ =
    -(rightSpan - rightOverhang) * rightTopChordPitch -
    topChordHeight * Math.sqrt(1 + rightTopChordPitch * rightTopChordPitch) +
    bottomChordOffset +
    bottomChordHeight;

  const bottomChordTopZ = Math.min(bottomChordLeftTopZ, bottomChordRightTopZ);

  return { bottomChordLeftTopZ, bottomChordRightTopZ, bottomChordTopZ };
}

export function generateBottomChord(
  leftSpan: UnsignedNumber,
  rightSpan: UnsignedNumber,
  leftOverhang: UnsignedNumber,
  rightOverhang: UnsignedNumber,
  leftTopChordPitch: UnsignedNumber,
  rightTopChordPitch: UnsignedNumber,
  topChordHeight: UnsignedNumber,
  bottomChordHeight: UnsignedNumber,
  bottomChordOffset: number,
): { bottomChord: PolyLine3; leftHeelChord: PolyLine3 | null; rightHeelChord: PolyLine3 | null } {
  const { bottomChordTopZ, bottomChordLeftTopZ, bottomChordRightTopZ } = getBottomChordTopZ(
    leftSpan,
    rightSpan,
    leftOverhang,
    rightOverhang,
    leftTopChordPitch,
    rightTopChordPitch,
    topChordHeight,
    bottomChordHeight,
    bottomChordOffset,
  );

  const rafterLeftVerticalDepth = topChordHeight * Math.sqrt(1 + leftTopChordPitch * leftTopChordPitch);
  const rafterRightVerticalDepth = topChordHeight * Math.sqrt(1 + rightTopChordPitch * rightTopChordPitch);

  const leftBottomChordXLimit = -(leftSpan - leftOverhang);
  const rightBottomChordXLimit = rightSpan - rightOverhang;
  const leftBottomChordX = Math.max(
    leftBottomChordXLimit,
    -(Math.abs(bottomChordTopZ) - rafterLeftVerticalDepth) / leftTopChordPitch,
  );
  const rightBottomChordX = Math.min(
    rightBottomChordXLimit,
    (Math.abs(bottomChordTopZ) - rafterRightVerticalDepth) / rightTopChordPitch,
  );

  const bottomChord = polyLine3New([leftBottomChordX, 0, bottomChordTopZ, rightBottomChordX, 0, bottomChordTopZ]);
  const leftHeelChord =
    bottomChordLeftTopZ > bottomChordTopZ
      ? polyLine3New([leftBottomChordX, 0, bottomChordTopZ, leftBottomChordX, 0, bottomChordLeftTopZ])
      : null;
  const rightHeelChord =
    bottomChordRightTopZ > bottomChordTopZ
      ? polyLine3New([rightBottomChordX, 0, bottomChordRightTopZ, rightBottomChordX, 0, bottomChordTopZ])
      : null;
  return { bottomChord, leftHeelChord, rightHeelChord };
}

export function generateCenterVerticalWebChord(
  webChordHalfHeight: UnsignedNumber,
  webChordBottomZ: number,
  ridgeRafterHalfHeight: UnsignedNumber,
): PolyLine3 {
  return polyLine3New([-webChordHalfHeight, 0, webChordBottomZ, -webChordHalfHeight, 0, -ridgeRafterHalfHeight]);
}

export function generateCenterDiagonalWebChords(
  webChordHalfHeight: UnsignedNumber,
  webChordBottomZ: number,
  leftSpan: UnsignedNumber,
  leftOverhang: UnsignedNumber,
  rightSpan: UnsignedNumber,
  rightOverhang: UnsignedNumber,
  leftTopChordPitch: UnsignedNumber,
  rightTopChordPitch: UnsignedNumber,
  topChordHalfHeight: UnsignedNumber,
): [PolyLine3, PolyLine3] {
  const costheta = 1 / Math.sqrt(1 + leftTopChordPitch * leftTopChordPitch);
  const sintheta = leftTopChordPitch * costheta;
  const leftWebChordTopX = -(leftSpan - leftOverhang) * 0.5 + topChordHalfHeight * sintheta;
  const leftWebChordTopZ = -(leftSpan - leftOverhang) * 0.5 * leftTopChordPitch - topChordHalfHeight * costheta;
  const leftWebChord = polyLine3New([leftWebChordTopX, 0, leftWebChordTopZ, -webChordHalfHeight, 0, webChordBottomZ]);

  const costhetaRight = 1 / Math.sqrt(1 + rightTopChordPitch * rightTopChordPitch);
  const sinthetaRight = rightTopChordPitch * costhetaRight;

  const rightWebChordTopX = (rightSpan - rightOverhang) * 0.5 - topChordHalfHeight * sinthetaRight;
  const rightWebChordTopZ =
    -(rightSpan - rightOverhang) * 0.5 * rightTopChordPitch - topChordHalfHeight * costhetaRight;
  const rightWebChord = polyLine3New([webChordHalfHeight, 0, webChordBottomZ, rightWebChordTopX, 0, rightWebChordTopZ]);
  return [leftWebChord, rightWebChord];
}

export function generateScissorWebChords(
  leftSpan: UnsignedNumber,
  leftOverhang: UnsignedNumber,
  rightSpan: UnsignedNumber,
  rightOverhang: UnsignedNumber,
  leftTopChordPitch: UnsignedNumber,
  rightTopChordPitch: UnsignedNumber,
  topChordHeight: UnsignedNumber,
  bottomChordHeight: UnsignedNumber,
  bottomChordOffset: number,
): {
  leftWebChord: PolyLine3;
  rightWebChord: PolyLine3;
  leftHeelChord: PolyLine3 | null;
  rightHeelChord: PolyLine3 | null;
} {
  const costhetaLeft = 1 / Math.sqrt(1 + leftTopChordPitch * leftTopChordPitch);
  const sinthetaLeft = leftTopChordPitch * costhetaLeft;
  const topChordHalfHeight = topChordHeight / 2;

  const leftWebChordTopZ = -(leftSpan - leftOverhang) * 0.5 * leftTopChordPitch - topChordHalfHeight * costhetaLeft;

  const costhetaRight = 1 / Math.sqrt(1 + rightTopChordPitch * rightTopChordPitch);
  const sinthetaRight = rightTopChordPitch * costhetaRight;
  const rightWebChordTopZ =
    -(rightSpan - rightOverhang) * 0.5 * rightTopChordPitch - topChordHalfHeight * costhetaRight;

  const webChordTopZ = Math.max(leftWebChordTopZ, rightWebChordTopZ);

  const leftWebChordTopX = -Math.abs(webChordTopZ) / leftTopChordPitch + topChordHalfHeight * sinthetaLeft;
  const rightWebChordTopX = Math.abs(webChordTopZ) / rightTopChordPitch - topChordHalfHeight * sinthetaRight;

  const { bottomChordTopZ, bottomChordLeftTopZ, bottomChordRightTopZ } = getBottomChordTopZ(
    leftSpan,
    rightSpan,
    leftOverhang,
    rightOverhang,
    leftTopChordPitch,
    rightTopChordPitch,
    topChordHeight,
    bottomChordHeight,
    bottomChordOffset,
  );

  const rightBottomChordX = Math.min(
    Math.abs(bottomChordTopZ) / rightTopChordPitch,
    (rightSpan - rightOverhang) * rightTopChordPitch,
  );

  const leftWebChord = polyLine3New([leftWebChordTopX, 0, webChordTopZ, rightBottomChordX, 0, bottomChordTopZ]);

  const leftBottomChordX = -Math.min(
    Math.abs(bottomChordTopZ) / leftTopChordPitch,
    (leftSpan - leftOverhang) * leftTopChordPitch,
  );

  const rightWebChord = polyLine3New([leftBottomChordX, 0, bottomChordTopZ, rightWebChordTopX, 0, webChordTopZ]);

  const leftHeelChord =
    bottomChordLeftTopZ > bottomChordTopZ
      ? polyLine3New([leftBottomChordX, 0, bottomChordTopZ, leftBottomChordX, 0, bottomChordLeftTopZ])
      : null;
  const rightHeelChord =
    bottomChordRightTopZ > bottomChordTopZ
      ? polyLine3New([rightBottomChordX, 0, bottomChordRightTopZ, rightBottomChordX, 0, bottomChordTopZ])
      : null;

  return { leftWebChord, rightWebChord, leftHeelChord, rightHeelChord };
}

function createSingleTopChordClipPlanes(chord: PolyLine3, clipPlaneDepth: UnsignedNumber): Plane[] {
  if (chord.length !== 6) {
    throw new Error("Single Top chord must be a polyline with 6 points");
  }
  const clipPlanes: Plane[] = [];
  const dx = chord[3] - chord[0];
  const dy = chord[4] - chord[1];
  const [dxNorm, dyNorm] = vec2Normalize(dx, dy);
  const [clipPlaneUx, clipPlaneUy, clipPlaneUz] = vec3Cross(dxNorm, dyNorm, 0, 0, 0, 1);

  const clipPlaneStart: Plane = planeNew(
    chord[0],
    chord[1],
    chord[2],
    -clipPlaneUx,
    -clipPlaneUy,
    -clipPlaneUz,
    0,
    0,
    -1,
  );
  clipPlanes.push(clipPlaneStart);
  const clipPlaneEnd: Plane = planeNew(chord[3], chord[4], chord[5], clipPlaneUx, clipPlaneUy, clipPlaneUz, 0, 0, -1);
  clipPlanes.push(clipPlaneEnd);

  let clipBottomOriginX = chord[0];
  let clipBottomOriginY = chord[1];
  let clipBottomOriginZ = chord[2] - clipPlaneDepth;

  if (chord[2] > chord[5]) {
    clipBottomOriginX = chord[3];
    clipBottomOriginY = chord[4];
    clipBottomOriginZ = chord[5] - clipPlaneDepth;
  }
  const clipBottomPlane: Plane = planeNew(clipBottomOriginX, clipBottomOriginY, clipBottomOriginZ, 1, 0, 0, 0, -1, 0);
  clipPlanes.push(clipBottomPlane);
  return clipPlanes;
}

export function projectChordsToFramingPlane(
  chords: (PolyLine3 | null)[],
  framingPlane: Plane,
  normalOffset: number = 0,
): (PolyLine3 | null)[] {
  const xzPlane = planeNewXZ();
  const offsetFramingPlane = planeMoveAlongNormal(planeCopy(framingPlane), normalOffset);
  return chords.map((p) => {
    if (!p) {
      return null;
    }
    const projected = planeProjectPolyLine3(xzPlane, p);
    return polyLine2OnPlane(offsetFramingPlane, projected);
  });
}

const xzPlane = planeNewXZ();

export function projectPointToFramingPlane(
  x: number,
  y: number,
  z: number,
  framingPlane: Plane,
): [number, number, number] {
  const projected = planeProjectPolyLine3(xzPlane, polyLine3New([x, y, z]));
  const projectedOnFramingPlane = polyLine2OnPlane(framingPlane, projected);
  return [projectedOnFramingPlane[0], projectedOnFramingPlane[1], projectedOnFramingPlane[2]];
}

export function setupTopChordRails(
  topChords: LumberPiecesByProfile,
  topChordPoints: PolyLine3 | null,
  clipPlaneDepth: UnsignedNumber,
  normX: number,
  normY: number,
  normZ: number,
): void {
  const { railsByType } = topChords;
  if (!railsByType[RoofFramingComponentType.TOP_CHORD]) {
    railsByType[RoofFramingComponentType.TOP_CHORD] = [];
  }

  if (!topChordPoints) {
    return;
  }

  const clipPlanes = createSingleTopChordClipPlanes(topChordPoints, clipPlaneDepth);

  railsByType[RoofFramingComponentType.TOP_CHORD].push(
    lumberRailNew(topChordPoints, normX, normY, normZ, clipPlanes, TOP_CHORD_CLIP_EXTENSION, TOP_CHORD_CLIP_EXTENSION),
  );
}

export function setupBottomChordRails(
  bottomChords: LumberPiecesByProfile,
  points: PolyLine3 | null,
  normX: number,
  normY: number,
  normZ: number,
): void {
  const { railsByType } = bottomChords;
  if (!railsByType[RoofFramingComponentType.BOTTOM_CHORD]) {
    railsByType[RoofFramingComponentType.BOTTOM_CHORD] = [];
  }

  if (!points) {
    return;
  }

  railsByType[RoofFramingComponentType.BOTTOM_CHORD].push(lumberRailNew(points, normX, normY, normZ));
}

export function setupWebChordRails(
  webChords: LumberPiecesByProfile,
  points: PolyLine3 | null,
  normX: number,
  normY: number,
  normZ: number,
): void {
  const { railsByType } = webChords;
  if (!railsByType[RoofFramingComponentType.WEB]) {
    railsByType[RoofFramingComponentType.WEB] = [];
  }

  if (!points) {
    return;
  }

  railsByType[RoofFramingComponentType.WEB].push(lumberRailNew(points, normX, normY, normZ));
}

export function setupJackChordRails(
  jackChords: LumberPiecesByProfile,
  jackChordPoints: PolyLine3 | null,
  clipPlaneDepth: UnsignedNumber,
  normX: number,
  normY: number,
  normZ: number,
): void {
  const { railsByType } = jackChords;
  if (!railsByType[RoofFramingComponentType.JACK]) {
    railsByType[RoofFramingComponentType.JACK] = [];
  }

  if (!jackChordPoints) {
    return;
  }

  const clipPlanes = createSingleTopChordClipPlanes(jackChordPoints, clipPlaneDepth);

  railsByType[RoofFramingComponentType.JACK].push(
    lumberRailNew(jackChordPoints, normX, normY, normZ, clipPlanes, TOP_CHORD_CLIP_EXTENSION, TOP_CHORD_CLIP_EXTENSION),
  );
}

export function generateHipTopChords(
  leftSpans: TrussMemberSpanData[],
  rightSpans: TrussMemberSpanData[],
  verticalOffset: number = 0,
): { left: PolyLine3; right: PolyLine3 } {
  if (leftSpans.length !== 2 || rightSpans.length !== 2) {
    throw new Error("Hip truss requires exactly two spans on each side");
  }

  // Calculate left side points
  const leftSlope = leftSpans[0];
  const leftFlat = leftSpans[1];

  // Start from outer edge (negative x)
  const leftX1 = -(leftSlope.span + leftFlat.span);
  const leftZ1 = -(leftSlope.span + leftFlat.span) * leftSlope.pitch + verticalOffset;

  // Transition point where slope meets flat
  const leftX2 = -leftFlat.span;
  const leftZ2 = -leftFlat.span * leftSlope.pitch + verticalOffset;

  // Calculate right side points
  const rightSlope = rightSpans[0];
  const rightFlat = rightSpans[1];

  // Start from outer edge (positive x)
  const rightX1 = rightSlope.span + rightFlat.span;
  const rightZ1 = -(rightSlope.span + rightFlat.span) * rightSlope.pitch + verticalOffset;

  // Transition point where slope meets flat
  const rightX2 = rightFlat.span;
  const rightZ2 = -rightFlat.span * rightSlope.pitch + verticalOffset;

  // Create polylines with proper ordering of points
  const leftTopChords = polyLine3New([
    leftX1,
    0,
    leftZ1, // Outer edge
    leftX2,
    0,
    leftZ2, // Transition point
    0,
    0,
    leftZ2, // Center point
  ]);

  const rightTopChords = polyLine3New([
    0,
    0,
    rightZ2, // Center point
    rightX2,
    0,
    rightZ2, // Transition point
    rightX1,
    0,
    rightZ1, // Outer edge
  ]);

  return { left: leftTopChords, right: rightTopChords };
}
